/**
 * `<VirtualizedTable>` — the shared windowed-table primitive (frontend.md §1.5, §5 step 6).
 *
 * The tabular sibling of `<VirtualizedList>`: a fixed header + a virtualized body. Used by
 * the action-item table, the failed-extraction triage table, the soft-deleted / conflicted
 * note tables — anywhere a surface shows rows with aligned columns at 100K-scale (D-009).
 *
 * Headless-ish: the caller passes `columns` (a header label + a per-row cell renderer +
 * an optional width) and `rows`. The virtualizer renders only the visible body window.
 * Like the list, `onEndReached` drives cursor pagination.
 */

import { useEffect, useRef } from 'react';
import { useVirtualizer } from '@tanstack/react-virtual';

export interface TableColumn<T> {
  /** The column id — also used as the React key. */
  id: string;
  /** Header content. A plain string for a static label, or any node (e.g. a sort button). */
  header: React.ReactNode;
  /** Per-cell render for this column. */
  cell: (row: T, index: number) => React.ReactNode;
  /** Optional fixed/min width — a CSS grid track value, e.g. '120px' or '1fr'. */
  width?: string;
}

interface VirtualizedTableProps<T> {
  columns: TableColumn<T>[];
  rows: T[];
  getKey: (row: T, index: number) => string;
  estimateRowHeight?: number;
  onEndReached?: () => void;
  endThreshold?: number;
  ariaLabel?: string;
  /** When set, the whole row is clickable (cursor + hover) and invokes this on click. */
  onRowClick?: (row: T, index: number) => void;
}

export function VirtualizedTable<T>({
  columns,
  rows,
  getKey,
  estimateRowHeight = 40,
  onEndReached,
  endThreshold = 8,
  ariaLabel,
  onRowClick,
}: VirtualizedTableProps<T>): JSX.Element {
  const scrollRef = useRef<HTMLDivElement | null>(null);

  const virtualizer = useVirtualizer({
    count: rows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => estimateRowHeight,
    overscan: 8,
  });

  const virtualRows = virtualizer.getVirtualItems();
  const lastRow = virtualRows[virtualRows.length - 1];

  useEffect(() => {
    if (!onEndReached || lastRow === undefined) return;
    if (lastRow.index >= rows.length - endThreshold) {
      onEndReached();
    }
  }, [lastRow, rows.length, endThreshold, onEndReached]);

  // Shared CSS grid template so the header and every body row align.
  const gridTemplateColumns = columns.map((c) => c.width ?? '1fr').join(' ');

  return (
    <div className="virtualized-table" role="table" aria-label={ariaLabel}>
      <div className="virtualized-table__header" role="row" style={{ gridTemplateColumns }}>
        {columns.map((col) => (
          <div key={col.id} className="virtualized-table__th" role="columnheader">
            {col.header}
          </div>
        ))}
      </div>
      <div
        ref={scrollRef}
        className="virtualized-table__body"
        style={{ overflow: 'auto', height: '100%' }}
      >
        <div
          style={{
            height: `${virtualizer.getTotalSize()}px`,
            width: '100%',
            position: 'relative',
          }}
        >
          {virtualRows.map((vr) => {
            const row = rows[vr.index];
            if (row === undefined) return null;
            return (
              <div
                key={getKey(row, vr.index)}
                role="row"
                ref={virtualizer.measureElement}
                data-index={vr.index}
                className={
                  'virtualized-table__row' +
                  (onRowClick ? ' virtualized-table__row--clickable' : '')
                }
                onClick={onRowClick ? () => onRowClick(row, vr.index) : undefined}
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  width: '100%',
                  transform: `translateY(${vr.start}px)`,
                  display: 'grid',
                  gridTemplateColumns,
                  ...(onRowClick ? { cursor: 'pointer' } : {}),
                }}
              >
                {columns.map((col) => (
                  <div key={col.id} role="cell" className="virtualized-table__td">
                    {col.cell(row, vr.index)}
                  </div>
                ))}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
